import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Chip,
  Button,
  Tooltip,
  Typography,
  IconButton
} from "@mui/material";

import {
  getAbsencePermissions,
  saveNewAbsencePermissions,
  editAbsencePermissions
} from './logic';

import {
  AddCircle,
  HourglassEmpty,
  CheckCircle,
  ZoomIn,
} from '@mui/icons-material';

import BackDrop from '../../shared/backdropDE';
import ErrorSnackbar from '../error-snackbar';
import DataTable from '../../shared/data-table';
import { ACTIONS, MESSAGES } from '../../shared/constants';
import { getUser } from '../../shared/auth-service';
import AbsencePermissionsDetail from './employee-absence-permissions-detail';
import dayjs from "dayjs";
import { useSnackbar } from 'notistack';

const EmployeeAbsencePermissions = () => {
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [dataItem, setDataItem] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();


  useEffect(() => {
    async function fetchData() {
      if (loading) {
        try {
          //const filters = [{employeeId: user.employeeId}, {airportId: user.airport.id}];
          //for(const item in user.airport){ airIds.push({airportId: user.airport[item].id}); }

          const filters = [{employeeId: user.employeeId}];
          const data = await getAbsencePermissions(filters);
          //console.log(data)
          setData(data);
          setLoading(false);
        } catch (error) {
          setError(error.message);
          setLoading(false);
          setData([]);
        }
      }
    }
    fetchData();
    return () => {};
  });

  // handles
  function formatDate(date) {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const handleNew = () => {
    setDataItem({
      employeeAbsencePermissionsId: 0,
      requirementType: 0,
      employeeId: user.employeeId,
      airportId: user.airport.id,
      absenceType: 0,
      absenceDescription: "",
      employeeArea: "",
      startDate: null,
      endDate: null,
      justify: "",
      response: "",
      isVerified: 0,
      isActive: 1,
      isNew: true,
      isRead: false
    });
    setShowModal(true);
  };

 const handleSave = async (send) => {
    setSaving(true);
    send.startDate = formatDate(send.startDate);
    send.endDate = formatDate(send.endDate);

    if (send.requirementType !== 1) {
      send.absenceType = null;
      send.absenceDescription = null;
    } else {
      if (send.absenceType !== 4) 
        send.absenceDescription = null;
    }

    await saveNewAbsencePermissions(send);
    enqueueSnackbar(MESSAGES.SAVED);

    setShowModal(false);
    setLoading(true);
 
    setSaving(false);
  };


 const handleEdit = async (edit) => {
    setSaving(true);
    edit.isVerify = edit.isVerified;
    delete edit.isVerified;
    await editAbsencePermissions(edit);
    enqueueSnackbar(MESSAGES.SAVED); 
    setShowModal(false);
    setLoading(true);
    setSaving(false);
  };


  const handleDataEdit = async (cell) => {
    cell.employeeId = cell.employee.employeeId;
    cell.airportId = cell.airport.airportId;
    cell.isNew = false;
    cell.isRead = true;
    setDataItem(cell);
    setShowModal(true);
  };

  const getRowId = (row) => row.employeeAbsencePermissionsId;

  // variables
  const typeRequest = [{type: 'Ausencia', color: 'info'}, {type: 'Permiso', color: 'warning'},{type: 'Cumpleaños', color: 'error'},{type: 'Día de la familia', color: 'success'}];

  const columns = [
    { field: "Ver", headerName: "Ver", width: 60, align: 'center', sortable: false,
      renderCell: (cellValues) => {
        return <Tooltip title="Ver detalle de solicitud">
          <IconButton
            color="success"
            onClick={(e) => { handleDataEdit(cellValues.row) }}
            >
            <ZoomIn />
          </IconButton>
        </Tooltip>;
      }
    },
    { field: 'requirementType', headerName: 'Solicitud', width: 120,
      renderCell: (cellValues) => {
        const { requirementType } = cellValues.row;
        const typeInfo = typeRequest[requirementType - 1];

        return (
          <Tooltip title={typeInfo.type} arrow>
            <Chip 
              label={typeInfo.type} 
              color={typeInfo.color} 
              variant="outlined" 
            />
          </Tooltip>
        );
      }
    },
    { field: 'employee.employeeId', headerName: 'Identificación', width: 110,
      valueGetter: (params) => params.row.employee.employeeId,
    },
    { field: 'employee.name', headerName: 'Nombre Completo', width: 280,
      //valueGetter: (params) => (params.row.employee.name.length) > 30 ? `${params.row.employee.name.slice(0, 30)}...` : params.row.employee.name,
      valueGetter: (params) => params.row.employee.name,
    },
    { field: 'employeeArea', headerName: 'Área', minwidth: 100, flex: 0.8 },
    { field: 'startDate', headerName: 'Inicio', width: 100, align: 'center',
      valueGetter: (params) => params.value.substring(0,10)
    },
    { field: 'endDate', headerName: 'Final', width: 100, align: 'center',
    valueGetter: (params) => params.value.substring(0,10)
    },
    { field: 'justify', headerName: 'Justificación', width: 200, flex: 0.8,
      valueGetter: (params) => params.row.justify,
    },
    { field: 'createdDate', headerName: 'Creado', width: 150, align: 'center',
      valueGetter: (params) => params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm") : ''
    },
    { 
      field: "isVerified", 
      headerName: "Estado", 
      width: 100, 
      align: 'center', 
      sortable: false,
      renderCell: (cellValues) => {
        return cellValues.row.isVerified ? (
          <Tooltip title="Verificado" arrow>
            <IconButton>
              <CheckCircle color='success' />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="En revisión" arrow>
            <IconButton>
              <HourglassEmpty color='warning' />
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  if (loading) { return (<BackDrop />); }
  if (error) { return (<ErrorSnackbar message={error} />); }

  return (
    <>
      {showModal && <AbsencePermissionsDetail
          data={dataItem}
          handleClose={setShowModal}
          handleSave={handleSave}
          handleEdit={handleEdit}
          saving={saving}
      />}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography style={{ color: '#00B0EF', fontWeight: 'bold', fontSize: '24px' }}>
            Permisos <span style={{ color: '#1A3072' }}>y Ausencias</span>
          </Typography>
        </Grid>
        <Box sx={{ my: 2, ml: 2 }}>
          <Button
            endIcon={<AddCircle />}
            variant="contained"
            onClick={() => { handleNew() }} 
            autoFocus
            >{ACTIONS.NEW}
          </Button>
        </Box>
        <DataTable 
          columns={columns} 
          rows={data} 
          getRowId={getRowId}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold'
            }
          }}
        />
      </Grid>
    </>
  );
};

export default EmployeeAbsencePermissions;
